import { Module } from 'vuex'

import { ArenaUseCase } from '@/application/arena/arenaUseCase'
import { ArenaProblemElement } from '@/domain/entities/arena/arenaProblemElement'
import { ArenaUserEntry } from '@/domain/entities/arena/arenaUserEntry'
import { ArenaUserSubmission } from '@/domain/entities/arena/arenaUserSubmission'
import { ArenaRepository } from '@/domain/repositories/arena/ArenaRepository'

interface ArenaState {
  arenaId: string
  arenaProblemId: string
  arenaProblemIndex: number
  arenaUserEntryList: ArenaUserEntry[]
  arenaProblemElementList: ArenaProblemElement[]
  arenaSolvedProblemList: Set<string>
  isLoading: boolean
  error: string | null
}

export const arenaModule = (repository: ArenaRepository): Module<ArenaState, any> => {
  const arenaUseCase = new ArenaUseCase(repository)

  return {
    namespaced: true,
    state: {
      arenaId: '',
      arenaProblemId: '',
      arenaProblemIndex: -1,
      arenaUserEntryList: [],
      arenaProblemElementList: [],
      arenaSolvedProblemList: new Set(),
      isLoading: false,
      error: null
    },
    mutations: {
      setArenaId(state, arenaId: string) {
        state.arenaId = arenaId
      },
      setArenaProblemId(state, arenaProblemId: string) {
        state.arenaProblemId = arenaProblemId
      },
      setArenaProblemIndex(state, arenaProblemIndex: number) {
        state.arenaProblemIndex = arenaProblemIndex
      },
      setArenaUserEntryList(state, arenaUserEntryList: ArenaUserEntry[]) {
        const newList = arenaUserEntryList.sort((a, b) => {
          return a.entryDate < b.entryDate ? 1 : -1
        })
        state.arenaUserEntryList = newList
      },
      setArenaProblemElementList(state, arenaProblemElementList: ArenaProblemElement[]) {
        state.arenaProblemElementList = arenaProblemElementList
      },
      setArenaSolvedProblemList(state, submissions: ArenaUserSubmission[]) {
        state.arenaSolvedProblemList.clear()
        for (let i = 0; i < submissions.length; i++) {
          if (submissions[i].status == 'ACCEPTED') state.arenaSolvedProblemList.add(submissions[i].problemId)
        }
      },
      setIsLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
      },
      setError(state, error: string | null) {
        state.error = error
      }
    },
    actions: {
      async getByUserIdArenaEntryList({ state, commit }, { userId }: { userId: string }) {
        // アリーナの一覧
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const res = await arenaUseCase.getByUserIdArenaEntryList(userId)
          commit('setArenaUserEntryList', res)
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      },
      async getArenaDetailData({ state, commit }, { arenaId, userId }: { arenaId: string; userId: string }) {
        // アリーナの詳細
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const res = await arenaUseCase.getByArenaIdProblemList(arenaId)
          commit('setArenaProblemElementList', res)
          const submissions = await arenaUseCase.getByUserIdArenaSubmissionList(arenaId, userId)
          commit('setArenaSolvedProblemList', submissions)
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      }
    },
    getters: {
      arenaId: state => state.arenaId,
      arenaProblemId: state => state.arenaProblemId,
      arenaProblemIndex: state => state.arenaProblemIndex,
      arenaUserEntryList: state => state.arenaUserEntryList,
      arenaProblemElementList: state => state.arenaProblemElementList,
      arenaSolvedProblemList: state => state.arenaSolvedProblemList,
      isLoading: state => state.isLoading,
      error: state => state.error
    }
  }
}
